<!-- Shows request contracts from keyword matches -->
<template>
  <v-card 
    v-if="isVisible" 
    :style="sectionStyle" 
    class="mb-6 section-card" 
    elevation="0"
  >
    <v-card-title class="pb-2">
      <h3 class="text-h6 font-weight-semibold d-flex align-center mb-0" style="color: #1f2937;">
        <v-icon class="mr-2" style="color: #6b7280;">mdi-magnify</v-icon>
        New Keyword Matches
      </h3>
    </v-card-title>
    <div class="px-4 pb-2">
      <p class="subtitle text-body mb-0" style="color: #6b7280;">Opportunities matching your search keywords</p>
    </div>

    <div v-if="isLoading" class="text-center py-8">
      <v-progress-circular indeterminate color="#ff1463"></v-progress-circular>
    </div>

    <div v-else-if="!matches || matches.length === 0" class="text-center py-8 grey--text">
      No keyword matches found
    </div>

    <v-card-text v-else class="pa-0">
      <div 
        v-for="(activity, index) in paginatedItems" 
        :key="`keyword-match-${activity.tender_id}-${index}`"
        class="match-card pa-6"
        :class="{ 'match-card-border-top': index > 0 }"
      >
        <div class="d-flex justify-space-between align-start">
          <div class="flex-grow-1">
            <div class="d-flex align-center gap-2 mb-2">
              <v-chip x-small color="orange" text-color="white">
                {{ activity.cluster_name }} ({{ activity.cluster_type }})
              </v-chip>
              <v-chip v-if="activity.value > 1000000" x-small color="blue" text-color="blue-darken-4">
                <v-icon x-small left>mdi-star</v-icon>
                MAJOR OPPORTUNITY
              </v-chip>
            </div>

            <div class="text-body-2 mb-2">
              <span class="font-weight-semibold">
                <router-link 
                  v-if="activity.publisher_id" 
                  :to="`/entity/${activity.publisher_id}`" 
                  class="text-decoration-none" 
                  style="color: inherit;"
                  target="_blank"
                >
                  {{ activity.publisher_name }}
                </router-link>
                <span v-else>{{ activity.publisher_name }}</span>
              </span>
              <span class="grey--text"> published a new tender</span>
            </div>

            <router-link 
              :to="`/tender/${activity.tender_id}`" 
              class="text-h6 font-weight-medium text-decoration-none d-flex align-center mb-3"
              style="color: #1f2937;"
              target="_blank"
            >
              {{ activity.title }}
              <v-icon small class="ml-2 grey--text">mdi-open-in-new</v-icon>
            </router-link>

            <div class="d-flex align-center gap-4 text-body-2 grey--text mb-3">
              <span class="d-flex align-center">
                <v-icon small class="mr-1">mdi-cash</v-icon>
                {{ formatCurrency(activity.value) }}
              </span>
              <span class="d-flex align-center">
                <v-icon small class="mr-1">mdi-calendar</v-icon>
                Published {{ formatDate(activity.contract_start) }}
              </span>
              <span 
                v-if="activity.closing_date" 
                class="d-flex align-center font-weight-semibold"
                :style="`color: ${getClosingDateColor(activity.closing_date)};`"
              >
                <v-icon small class="mr-1">mdi-alarm</v-icon>
                Closes: {{ formatDate(activity.closing_date) }} ({{ getDaysUntilClose(activity.closing_date) }} days)
              </span>
            </div>

            <div 
              v-if="activity.value > 1000000" 
              class="pa-3" 
              style="background-color: #d1fae5; border-radius: 4px;"
            >
              <div class="text-body-2" style="color: #4b5563; font-style: italic; font-size: 13px;">
                This is a major opportunity worth {{ formatCurrency(activity.value) }}.
              </div>
            </div>
          </div>

          <div class="d-flex flex-column ml-4" style="gap: 8px;">
            <v-btn
              small
              depressed
              dark
              class="body-2 text-capitalize"
              color="#10b981"
              text-color="white"
              style="font-size: 13px;"
              :to="`/tender/${activity.tender_id}`"
              target="_blank"
            >
              <v-icon small left>mdi-eye</v-icon>
              View Tender
            </v-btn>
            <v-btn
              small
              outlined
              color="primary"
              class="text-capitalize body-2"
              style="font-size: 13px;"
              @click.stop="handleAddToPipeline(activity)"
            >
              <v-icon small left>mdi-bookmark-plus</v-icon>
              Add to Pipeline
            </v-btn>
          </div>
        </div>
      </div>

      <!-- Pagination -->
      <pagination-controls
        :show="showPagination"
        :text="paginationText"
        :can-prev="canGoToPreviousPage"
        :can-next="canGoToNextPage"
        @prev="goToPreviousPage"
        @next="goToNextPage"
      />
    </v-card-text>
  </v-card>
</template>

<script>
import PaginationMixin from '../mixins/PaginationMixin.js';
import PaginationControls from '../PaginationControls.vue';
import { formattedCurrency, formatDateReadable } from '../Utils.js';

export default {
  name: 'KeywordMatchesSection',
  
  mixins: [PaginationMixin],

  components: {
    PaginationControls
  },

  props: {
    matches: {
      type: Array,
      default: () => []
    },
    isLoading: {
      type: Boolean,
      default: false
    },
    sectionStyle: {
      type: String,
      default: ''
    },
    isVisible: {
      type: Boolean,
      default: true
    },
    itemsPerPage: {
      type: Number,
      default: 10
    }
  },

  computed: {
    // Items used by the shared pagination mixin
    paginationItems() {
      return this.matches || [];
    }
  },

  methods: {
    handleAddToPipeline(activity) {
      this.$emit('add-to-pipeline', activity);
    },

    formatCurrency(value) {
      return formattedCurrency(value);
    },

    formatDate(dateStr) {
      return formatDateReadable(dateStr);
    },

    getClosingDateColor(closingDate) {
      const days = this.getDaysUntilClose(closingDate);
      if (days <= 7) return '#dc2626';
      if (days <= 14) return '#d97706';
      return '#059669';
    },

    getDaysUntilClose(closingDate) {
      if (!closingDate) return 0;
      try {
        const closeDate = new Date(closingDate);
        const today = new Date();
        const diffTime = closeDate - today;
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return diffDays;
      } catch (e) {
        return 0;
      }
    }
  }
};
</script>

<style scoped>
.match-card {
  cursor: pointer;
  border-bottom: 1px solid #e5e7eb;
  background-color: #f0fdf4;
  border-left: 3px solid #4ade80;
  transition: background-color 0.2s ease;
}

.match-card:hover {
  background-color: #e8f5e9;
}

.match-card-border-top {
  border-top: 1px solid #e5e7eb;
}
</style>
